import React from "react";

import { Navigate, useLocation, useParams } from "react-router-dom";

import { hasAccessToken } from "../../Services/SessionStore";
import { useAppSelector } from "../../Store/Hooks";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

function useEmailTenantSlug() {
  const { user, isAuthenticated } = useAppSelector(
    state => state.authentication
  );
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const isUserAuthenticated =
    isAuthenticated || hasAccessToken() || !!loggedInUser;

  const tenantSlug =
    loggedInUser?.slug || user?.user?.tenant_slug || user?.user?.slug || null;
  const isIzbaAdmin = isRole(
    user?.user?.role || loggedInUser?.role,
    ROLES.IZBA_ADMIN
  );

  return {
    isUserAuthenticated,
    tenantSlug,
    isIzbaAdmin,
  };
}

interface EmailTenantRedirectProps {
  targetPath: string;
  /** Map legacy exec-ops tab=assessment → assessments; default tab when missing. */
  normalizeOpsAssessmentTab?: boolean;
}

function buildRedirectSearch(
  search: string,
  normalizeOpsAssessmentTab?: boolean
): string {
  if (!normalizeOpsAssessmentTab) {
    return search;
  }
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  if (!tab || tab === "assessment") {
    params.set("tab", "assessments");
  }
  const next = params.toString();
  return next ? `?${next}` : "?tab=assessments";
}

/**
 * Email-compat redirect: /{path} or /:orgSlug/{path} → /:orgSlug/{targetPath}
 * (query preserved; optional Ops tab normalization).
 */
export const EmailTenantRedirect: React.FC<EmailTenantRedirectProps> = ({
  targetPath,
  normalizeOpsAssessmentTab = false,
}) => {
  const location = useLocation();
  const { orgSlug: paramSlug } = useParams<{ orgSlug?: string }>();
  const { isUserAuthenticated, tenantSlug, isIzbaAdmin } = useEmailTenantSlug();
  const orgSlug = paramSlug || tenantSlug;

  if (!isUserAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (isIzbaAdmin || !orgSlug) {
    return <Navigate to="/login" replace />;
  }

  const normalizedPath = targetPath.startsWith("/")
    ? targetPath
    : `/${targetPath}`;
  const search = buildRedirectSearch(
    location.search,
    normalizeOpsAssessmentTab
  );

  return <Navigate to={`/${orgSlug}${normalizedPath}${search}`} replace />;
};

/**
 * Email-compat redirect: /guidance-library → /:orgSlug/guidance-library (query preserved).
 */
const SponsorTipsRedirect: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const { isUserAuthenticated, tenantSlug, isIzbaAdmin } = useEmailTenantSlug();

  if (!isUserAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (isIzbaAdmin || !tenantSlug) {
    return <Navigate to="/login" replace />;
  }

  const target = slug
    ? `/${tenantSlug}/guidance-library/${slug}${location.search}`
    : `/${tenantSlug}/guidance-library${location.search}`;

  return <Navigate to={target} replace />;
};

export default SponsorTipsRedirect;
